import "./App.css";
import { useState, useEffect } from "react";
import Loader from "./components/loader";
import LocBlock from "./components/locblock";
import { environment } from "./EnvDev";
import foPin from "./fo-pin.png";
import gfPin from "./gf-pin.png";
import moPin from "./mo-pin.png";
import pcPin from "./pc-pin.png";
import pwPin from "./pw-pin.png";
import pwLogo from "./pw-logo.png";
import gfLogo from "./gf-logo.png";
import pcLogo from "./pc-logo.png";
import moLogo from "./mo-logo.png";
import foLogo from "./fo-logo.png";

const CLOSER_ENDPOINTS = {
  Gforce: "https://www.gogforce.com/wp-json/locs/closer",
  "Mach One": "https://www.mach1epoxy.com/wp-json/locs/closer",
  "Paint Corps": "https://www.paintcorps.com//wp-json/locs/closer",
  "Field Ops": "https://www.gofieldops.com/wp-json/locs/closer",
  "Prop Wash": "https://www.propwash.com/wp-json/locs/closer",
};

/** Order matches the full sequential brand chain (G-FORCE → … → Prop Wash). */
const ALL_SERVICE_KEYS = [
  "Gforce",
  "Paint Corps",
  "Mach One",
  "Field Ops",
  "Prop Wash",
];

const SERVICE_CHECKBOXES = [
  { key: "Gforce", label: "Pavement Markings" },
  { key: "Mach One", label: "Epoxy Floors" },
  { key: "Paint Corps", label: "Painting" },
  { key: "Prop Wash", label: "Pressure Washing" },
  { key: "Field Ops", label: "Athletic Field & Court Markings" },
];

function allServicesSelected(keys) {
  if (keys.length !== ALL_SERVICE_KEYS.length) return false;
  return ALL_SERVICE_KEYS.every((k) => keys.includes(k));
}

const LEGEND_ITEMS = [
  { key: "gf", pin: gfPin, label: "G-FORCE®", alt: "G-FORCE" },
  { key: "mo", pin: moPin, label: "MACH ONE®", alt: "MACH ONE" },
  { key: "pc", pin: pcPin, label: "PAINT CORPS®", alt: "PAINT CORPS" },
  { key: "pw", pin: pwPin, label: "PROP WASH®", alt: "PROP WASH" },
  { key: "fo", pin: foPin, label: "FIELD OPS®", alt: "FIELD OPS" },
];

/** Map API `brand` string to a stable legend key (see LEGEND_ITEMS). */
function legendKeyFromBrand(brand) {
  const raw = String(brand ?? "").toUpperCase();
  const compact = raw.replace(/[®️\s-]/g, "");
  if (compact.includes("GFORCE") || raw.includes("G-FORCE")) return "gf";
  if (compact.includes("MACHONE") || raw.includes("MACH ONE")) return "mo";
  if (compact.includes("PAINTCORPS") || raw.includes("PAINT CORPS")) return "pc";
  if (compact.includes("FIELDOPS") || raw.includes("FIELD OPS")) return "fo";
  if (compact.includes("PROPWASH") || raw.includes("PROP WASH")) return "pw";
  return null;
}

function App() {
  const [loading, setLoading] = useState(false);
  const [locs, setLocs] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [zip, setZip] = useState("");
  const [status, setStatus] = useState("");
  const [selectedServices, setSelectedServices] = useState(() => [
    ...ALL_SERVICE_KEYS,
  ]);

  const handleSubmission = (e) => {
    e.preventDefault();
    setLocs([]);
    setNotFound(false);
    setLoading(true);
    geoLocate(zip);
  };

  const handleServiceToggle = (key) => {
    setSelectedServices((prev) => {
      if (prev.includes(key)) {
        if (prev.length <= 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const fetchSelectedServicesParallel = async (lat, long, keys) => {
    setLoading(true);
    setLocs([]);
    setStatus("");

    try {
      const results = await Promise.all(
        keys.map((k) =>
          fetch(`${CLOSER_ENDPOINTS[k]}?lat=${lat}&lng=${long}`).then((r) =>
            r.json()
          )
        )
      );

      let merged = [];
      for (const data of results) {
        if (Array.isArray(data)) {
          merged = merged.concat(data);
        }
      }
      merged = applyLocalPins(merged);
      sortByDistance(merged);

      if (merged.length === 0) {
        setNotFound(true);
        setLocs([]);
      } else {
        setNotFound(false);
        setLocs(merged);
      }
    } catch (error) {
      console.error("Error fetching selected services:", error);
      setNotFound(true);
      setLocs([]);
    } finally {
      setLoading(false);
    }
  };

  const runLocationSearch = (lat, long, keys) => {
    if (!keys.length) {
      setLocs([]);
      setNotFound(true);
      setLoading(false);
      setStatus("");
      return;
    }
    if (allServicesSelected(keys)) {
      getGforceCloserLocs(lat, long);
      return;
    }
    fetchSelectedServicesParallel(lat, long, keys);
  };

  const geoLocate = async (zip) => {
    const call = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places-permanent/${zip}.json?access_token=${environment.mapbox.accessToken}&country=US`
    );
    const response = await call.json();

    if (response.features.length === 0) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    const coords = response.features[0].geometry.coordinates;
    const [lat, long] = [coords[1], coords[0]];

    runLocationSearch(lat, long, selectedServices);
  };
  var locations = [];

  // Function to sort an array by distance
  const sortByDistance = (arr) => arr.sort((a, b) => a.distance - b.distance);

  const brandToPin = {
    "FIELD OPS": foPin,
    "G-FORCE": gfPin,
    "MACH ONE": moPin,
    "PAINT CORPS": pcPin,
    "PROP WASH": pwPin,
    "PROP WASH®": pwPin,
  };

  const applyLocalPins = (arr) =>
    (arr || []).map((loc) => ({
      ...loc,
      pin: brandToPin[loc?.brand] ?? loc?.pin,
    }));

  // Function to merge two sorted arrays by distance
  const mergeSortedArrays = (arr1, arr2) => {
    let merged = [];
    let i = 0,
      j = 0;

    while (i < arr1.length && j < arr2.length) {
      if (arr1[i].distance < arr2[j].distance) {
        merged.push(arr1[i]);
        i++;
      } else {
        merged.push(arr2[j]);
        j++;
      }
    }

    // Add remaining items from either array
    return merged.concat(arr1.slice(i)).concat(arr2.slice(j));
  };

  // Function calls
  const getGforceCloserLocs = async (lat, long) => {
    setStatus(
      <>
        <img
          src={gfLogo}
          height="20"
          alt="G-FORCE"
          className="inline-block mr-2 w-auto h-10"
        />
        Calling G-FORCE®
      </>
    );
    setLoading(true);

    try {
      const call = await fetch(
        `https://www.gogforce.com/wp-json/locs/closer?lat=${lat}&lng=${long}`
      );
      const response = await call.json();
      const sortedResponse = sortByDistance(applyLocalPins(response));
      await getPaintCorpsLocs(lat, long, sortedResponse);
    } catch (error) {
      console.error("Error fetching Gforce locations:", error);
      await getPaintCorpsLocs(lat, long, []);
    }
  };

  const getPaintCorpsLocs = async (lat, long, gforceData) => {
    setStatus(
      <>
        <img
          src={pcLogo}
          height="20"
          alt="Paint Corps"
          className="inline-block mr-2 w-auto h-10"
        />
        Calling PAINT CORPS®
      </>
    );
    try {
      const call = await fetch(
        `https://www.paintcorps.com//wp-json/locs/closer?lat=${lat}&lng=${long}`
      );
      const response = await call.json();

      // Sort and merge
      const sortedResponse = sortByDistance(applyLocalPins(response));
      const mergedData = mergeSortedArrays(gforceData, sortedResponse);

      // Pass the merged data to the next function
      await getMachOneLocs(lat, long, mergedData);
    } catch (error) {
      console.error("Error fetching PaintCorps locations:", error);
      await getMachOneLocs(lat, long, gforceData);
    }
  };

  const getMachOneLocs = async (lat, long, mergedData) => {
    setStatus(
      <>
        <img
          src={moLogo}
          height="20"
          alt="Mach One"
          className="inline-block mr-2 w-auto h-10"
        />
        Calling MACH ONE®
      </>
    );

    try {
      const call = await fetch(
        `https://www.mach1epoxy.com/wp-json/locs/closer?lat=${lat}&lng=${long}`
      );
      const response = await call.json();

      // Sort and merge
      const sortedResponse = sortByDistance(applyLocalPins(response));
      const newMergedData = mergeSortedArrays(mergedData, sortedResponse);

      // Pass the merged data to the next function
      await getFieldOpsLocs(lat, long, newMergedData);
    } catch (error) {
      console.error("Error fetching MACH ONE locations:", error);
      await getFieldOpsLocs(lat, long, mergedData);
    }
  };

  const getPropWashLocs = async (lat, long, mergedData) => {
    setStatus(
      <>
        <img
          src={pwLogo}
          height="20"
          alt="Prop Wash"
          className="inline-block mr-2 w-auto h-10"
        />
        Calling PROP WASH®
      </>
    );

    try {
      const call = await fetch(
        `https://www.propwash.com/wp-json/locs/closer?lat=${lat}&lng=${long}`
      );
      const response = await call.json();
      const sortedResponse = sortByDistance(applyLocalPins(response));
      locations = mergeSortedArrays(mergedData, sortedResponse);

      if (locations.length === 0) {
        setNotFound(true);
        setStatus("");
      } else {
        setNotFound(false);
        setStatus("");
      }

      locations = sortByDistance(locations);
      setLocs(applyLocalPins(locations));
    } catch (error) {
      console.error("Error fetching Prop Wash locations:", error);
      if (mergedData.length === 0) {
        setNotFound(true);
      } else {
        setNotFound(false);
        setLocs(applyLocalPins(sortByDistance(mergedData)));
      }
      setStatus("");
    } finally {
      setLoading(false);
    }
  };

  const getFieldOpsLocs = async (lat, long, mergedData) => {
    setStatus(
      <>
        <img
          src={foLogo}
          height="20"
          alt="Field Ops"
          className="inline-block mr-2 w-auto h-10"
        />
        Calling FIELD OPS®
      </>
    );

    try {
      const call = await fetch(
        `https://www.gofieldops.com/wp-json/locs/closer?lat=${lat}&lng=${long}`
      );
      const response = await call.json();
      console.log(response);

      const sortedResponse = sortByDistance(applyLocalPins(response));
      locations = mergeSortedArrays(mergedData, sortedResponse);

      if (locations.length === 0) {
        setNotFound(true);
        setStatus("");
      } else {
        setNotFound(false);
        setStatus("");
      }

      locations = sortByDistance(locations);
      await getPropWashLocs(lat, long, locations);
    } catch (error) {
      console.error("Error fetching Field Ops locations:", error);
      await getPropWashLocs(lat, long, mergedData);
    }
  };

  useEffect(() => {
    // Parse the URL query parameters
    const searchParams = new URLSearchParams(window.location.search);
    const zipParam = searchParams.get("zip"); // Get the value of the 'zip' parameter

    if (zipParam) {
      setZip(zipParam); // Set the input value to the 'zip' parameter
      geoLocate(zipParam); // Trigger geolocation and subsequent actions
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount for ?zip=
  }, []);

  useEffect(() => {
    if (locs.length === 0) return;
    const coords = Array.isArray(locs[0].coords)
      ? locs[0].coords
      : [locs[0].coords.lng, locs[0].coords.lat];
    const [long, lat] = coords;
    runLocationSearch(lat, long, selectedServices);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run search when service checkboxes change; locs only seeds coords
  }, [selectedServices]);

  const handleInputChange = (event) => {
    setZip(event.target.value); // Update the state when the input value changes
  };

  return (
    <div className="container px-4 pt-9 pb-4 mx-auto w-full bg-gray-50 md:pt-16">
      <div className="gap-6 items-center lg:grid lg:grid-cols-12">
        <div className="mb-5 lg:col-span-6 md:mb-0">
          <h1 className="locations__title">
            Find a Veteran-Owned Service Provider Near You!
          </h1>
          <div className="locations__description">
            <p>
              Use our location search below to connect with G-FORCE®, MACH ONE®,
              PAINT CORPS®, FIELD OPS®, and PROP WASH® professionals in your area,
              delivering expert solutions with military precision.
            </p>
          </div>
          <div className="locations__breadcrumbs">
            <div class="breadcrumbs">
              <div class="breadcrumbs">
                <p id="breadcrumbs">
                  <span>
                    <span><a href="https://staging.veteranservicebrands.com/">Home</a></span> / <span class="breadcrumb_last" aria-current="page">Location</span></span></p>
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-hidden lg:col-span-6 max-w-[558px] mx-auto lg:mx-0 mt-10 lg:mt-0">
          <img
            src="http://www.veteranservicebrands.com/wp-content/uploads/2025/02/map-vsb.png"
            alt="Map of the United States with all Veteran Service Brands Locations"
            className="w-full"
          />
        </div>
      </div>
      
        <form id="locator-zip" className="px-6 py-2 mt-8 bg-white">
          <div
            className="block mt-8 mb-3 w-full text-left xs:text-xs md:text-sm lg:text-base"
            id="filters"
          >
            <label className="block mb-4 text-base font-bold uppercase">
              What service are you looking for?
            </label>
            {SERVICE_CHECKBOXES.map(({ key, label }, idx) => (
              <label
                key={key}
                className={`block md:!inline-block md:w-auto${
                  idx > 0 && idx < SERVICE_CHECKBOXES.length - 1
                    ? " md:ml-3"
                    : " md:ml-3"
                }`}
              >
                <input
                  type="checkbox"
                  name={`service-${key}`}
                  className="mr-1 locator-service-checkbox"
                  checked={selectedServices.includes(key)}
                  onChange={() => handleServiceToggle(key)}
                />
                {label}
              </label>
            ))}
          </div>
          <div className="flex flex-col md:flex-row justify-center items-center gap-[12px] md:gap-[24px] w-full">
            <div className="block relative md:inline-block zip">
              <input
                className="!w-full rounded-sm py-3 pl-4 pr-8 border border-[#070815] focus:outline-none duration-200 transition-all text-gray-600"
                type="text"
                autoComplete="off"
                id="zip"
                aria-label="zip"
                onChange={handleInputChange}
                value={zip}
                placeholder="Enter your zip code"
              />
              {loading && <Loader />}
            </div>

            <button
              type="submit"
              onClick={handleSubmission}
              className="w-full shrink-0 bg-[#264583] md:!w-[185px] uppercase inline-block text-white h-[50px] py-3 border border-[#264583] px-3 text-sm font-semibold rounded-sm transition-all duration-200"
            >
              {loading ? "Locating" : "Find a Location"}
            </button>
          </div>
        </form>
        {status !== "" && (
          <span className="block py-2 mt-6 w-full text-sm text-center text-gray-800">
            {status}
          </span>
        )}
        {notFound && (
          <div className="p-5 mt-10 border border-gray-600">
            <span className="text-2xl uppercase">
              There are no locations in your search area
            </span>
            <p>Please try a different search zip code or city.</p>
          </div>
        )}
      
      {locs.length > 0 && (
        <div className="text-center lg:col-span-6 lg:col-start-4">
          {notFound && (
            <div className="p-5 mt-10 border border-gray-600">
              <span className="text-2xl uppercase">
                There are no locations in your search area
              </span>
              <p>Please try a different search zip code or city.</p>
            </div>
          )}
        </div>
      )}
      
      {locs.length > 0 && (
        <>
          <div className="grid grid-cols-6 gap-x-10 mt-3 mb-12">
            <div className="col-span-6 pl-4">
              <span className="block mt-10 mb-3 font-bold text-center uppercase md:mt-0 resultsnumber" style={{ fontSize: '2.25rem', lineHeight: '2.5rem' }}>
                {locs.length} LOCATIONS FOUND
              </span>
              <div className="flex flex-wrap gap-6 justify-center items-center">
                {LEGEND_ITEMS.filter((item) =>
                  locs.some((loc) => legendKeyFromBrand(loc.brand) === item.key)
                ).map((item) => (
                  <div
                    key={item.key}
                    className="inline-block text-xs font-bold"
                  >
                    <img
                      src={item.pin}
                      className="inline-block"
                      width="16"
                      alt={`${item.alt} pin legend`}
                    />{" "}
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="locations__map">
            <div className="locations__map--left results">
              <div className="locs-list h-full max-h-[650px] overflow-y-auto">
                {locs.map((loc, index) => (
                  <div className="locs-list__item" key={index}>
                    <LocBlock loc={loc} />
                  </div>
                ))}
              </div>
              <span className="block mt-6 text-sm text-center text-gray-500">
                Area Searched: 150 Mile Radius
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default App;
